/**
 * Dot-path diff between two plain objects (matches react-hook-form naming).
 * Shared by editor UI and save overlay so “edited” detection stays consistent.
 */
import isEmpty from "lodash-es/isEmpty";

export const getEditedPaths = (
  current: unknown,
  original: unknown,
  prefix = "",
): Set<string> => {
  const paths = new Set<string>();

  const pushIfDifferent = (path: string) => {
    if (!isEmpty(path)) paths.add(path);
  };

  const walk = (cur: unknown, orig: unknown, path: string) => {
    if (cur === orig) return;
    if (cur == null || orig == null) {
      if (cur !== orig) pushIfDifferent(path);
      return;
    }
    if (typeof cur !== "object" || typeof orig !== "object") {
      if (cur !== orig) pushIfDifferent(path);
      return;
    }
    if (Array.isArray(cur) && Array.isArray(orig)) {
      const maxLen = Math.max(cur.length, orig.length);
      for (let i = 0; i < maxLen; i++) {
        const key = String(i);
        const p = path ? `${path}.${key}` : key;
        walk(cur[i], orig[i], p);
      }
      return;
    }
    if (Array.isArray(cur) || Array.isArray(orig)) {
      pushIfDifferent(path);
      return;
    }
    const curObj = cur as Record<string, unknown>;
    const origObj = orig as Record<string, unknown>;
    const allKeys = new Set([...Object.keys(curObj), ...Object.keys(origObj)]);
    for (const key of allKeys) {
      const p = path ? `${path}.${key}` : key;
      walk(curObj[key], origObj[key], p);
    }
  };

  walk(current, original, prefix);
  return paths;
};

/**
 * Form labels for content sections that have edits.
 * Paths use numeric indices (content.0, …); map via current.content[index].formLabel.
 */
export const getEditedContentLabels = (
  current: unknown,
  original: unknown,
): string[] => {
  const paths = getEditedPaths(current, original);
  const labels = new Set<string>();
  const cur = current as { content?: Array<{ formLabel?: string }> } | null;
  const content = cur?.content;
  if (!Array.isArray(content)) return Array.from(labels);
  for (const path of paths) {
    if (!path.startsWith("content.")) continue;
    const segment = path.split(".")[1];
    if (isEmpty(segment)) continue;
    const idx = parseInt(segment, 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= content.length) continue;
    const label = content[idx]?.formLabel;
    if (typeof label === "string" && !isEmpty(label)) labels.add(label);
  }
  return Array.from(labels);
};
